import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { TenantProvider } from '@/contexts/TenantContext';
import { OrganisationProvider } from '@/contexts/OrganisationContext';
import { LoadingScreen } from '@/components/LoadingScreen';
import { LoginPrompt } from '@/components/LoginPrompt';
import { MainApp } from '@/components/MainApp';
import DemoIndex from '@/pages/DemoIndex';
import DemoLogin from '@/pages/DemoLogin';
import DemoDashboard from '@/pages/DemoDashboard';
import ResetPassword from '@/pages/ResetPassword';

const EmbeddedSimulation = lazy(() => import('@/pages/EmbeddedSimulation'));
const EmbeddedHistory = lazy(() => import('@/pages/EmbeddedHistory'));
import { Sentry } from '@/lib/sentry';
import { ErrorFallback } from '@/components/ErrorFallback';
import { ChunkErrorBoundary } from '@/components/ChunkErrorBoundary';

const queryClient = new QueryClient();

function AuthenticatedApp() {
  const { user, loading, error } = useAuth();

  // Loading state
  if (loading) {
    return <LoadingScreen />;
  }

  // Not authenticated
  if (!user) {
    return <LoginPrompt error={error} />;
  }

  // Authenticated - show the main app
  return (
    <OrganisationProvider>
      <MainApp />
    </OrganisationProvider>
  );
}

const App = () => (
  <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog={false}>
    <ChunkErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Public Demo Routes */}
            <Route path="/demo" element={<DemoIndex />} />
            <Route path="/demo/:companySlug" element={<DemoLogin />} />
            <Route path="/demo/:companySlug/dashboard" element={<DemoDashboard />} />
            
            {/* Public Password Reset Route */}
            <Route path="/reset-password" element={
              <TenantProvider>
                <AuthProvider>
                  <ResetPassword />
                </AuthProvider>
              </TenantProvider>
            } />

            {/* Embed route — no sidebar/header. Auth via ?token= query param (WordPress SSO) */}
            <Route path="/embed/simulation/:id" element={
              <TenantProvider>
                <AuthProvider>
                  <Suspense fallback={<LoadingScreen />}>
                    <EmbeddedSimulation />
                  </Suspense>
                </AuthProvider>
              </TenantProvider>
            } />

            {/* Embed history — lists the GP's past simulation sessions for a dedicated WP page */}
            <Route path="/embed/history" element={
              <TenantProvider>
                <AuthProvider>
                  <Suspense fallback={<LoadingScreen />}>
                    <EmbeddedHistory />
                  </Suspense>
                </AuthProvider>
              </TenantProvider>
            } />

            {/* Main App (with authentication) */}
            <Route path="/*" element={
              <TenantProvider>
                <AuthProvider>
                  <AuthenticatedApp />
                </AuthProvider>
              </TenantProvider>
            } />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    </ChunkErrorBoundary>
  </Sentry.ErrorBoundary>
);

export default App;
